import React from 'react';
import { Card } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Callout } from '../../../components/ui/Callout';
import { Icon } from '../../../components/ui/Icon';
import { Divider } from '../../../components/ui/Divider';
import { Field } from '../../../components/ui/Field';
import { Input, Textarea } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { useShippingWizard, ShippingSteps } from '../hooks/useShippingWizard';
import { useShippingHandlers } from '../hooks/useShippingHandlers';
import { fedexPackagingOptions } from '../constants/shippingOptions';

interface Props {
  wizard: ReturnType<typeof useShippingWizard>;
  handlers: ReturnType<typeof useShippingHandlers>;
}

export const ShipStepReview: React.FC<Props> = ({ wizard: w, handlers: h }) => {
  const {
    locationState,
    parcels,
    documents,
    parcelDetails,
    selectedService,
    shipmentPurpose,
    shippingDescription,
    setShippingDescription,
    vatType,
    vatNumber,
    signatureRequired,
    setSignatureRequired,
    defaultShippingId,
    setDefaultShippingId,
    defaultShippingName,
    setDefaultShippingName,
    senderIdTouched,
    setSenderIdTouched,
    senderNameTouched,
    setSenderNameTouched,
    errorMessage,
    setErrorMessage,
    isUpdatingRates,
    stepValidation,
    formattedShippingCost,
    formattedInsuranceCost,
    formattedTotalCost,
    formattedSignatureCost,
    handleProceedToPayment,
  } = w;

  const totalItemValue = parcels.reduce(
    (total, parcel) =>
      total + (parcel.items?.reduce((pt, item) => pt + item.price * item.quantity, 0) || 0),
    0
  );

  const srcAddr = locationState?.selectedSourceAddress;
  const dstAddr = locationState?.selectedDestinationAddress;

  const countryName = (addr: any) =>
    typeof addr?.country === 'object' ? addr?.country?.name : addr?.country || '';

  return (
    <div>
      <Card pad style={{ padding: 28 }}>
        <h2 style={{ fontSize: 20, marginBottom: 4 }}>Order Summary</h2>
        <p className="muted" style={{ fontSize: 13, marginBottom: 20 }}>
          Please review all details before proceeding to payment.
        </p>

        {errorMessage && (
          <div style={{ marginBottom: 16 }}>
            <Callout tone="warn">
              <Icon name="alertCircle" size={14} />
              <span style={{ fontSize: 13 }}>{errorMessage}</span>
            </Callout>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 20 }} className="details-grid">
          {/* Left column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Addresses + Sender Verification */}
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', background: 'var(--surface-2)', fontWeight: 600, fontSize: 14 }}>
                Addresses
              </div>
              <div style={{ padding: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--blue)', marginBottom: 4 }}>
                      Ship From
                    </div>
                    <div style={{ fontSize: 13 }}>{srcAddr?.contactName}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {srcAddr?.street} {srcAddr?.houseNo}
                    </div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {countryName(srcAddr)}, {srcAddr?.zipCode}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--blue)', marginBottom: 4 }}>
                      Deliver To
                    </div>
                    <div style={{ fontSize: 13 }}>{dstAddr?.contactName}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {dstAddr?.street} {dstAddr?.houseNo}
                    </div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {countryName(dstAddr)}, {dstAddr?.zipCode}
                    </div>
                  </div>
                </div>

                <Divider style={{ margin: '12px 0' }} />

                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--blue)', marginBottom: 12 }}>
                  Sender Verification
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field
                    label="Sender ID Number"
                    hint={senderIdTouched && !defaultShippingId.trim() ? 'Sender ID number is required' : undefined}
                  >
                    <Input
                      value={defaultShippingId}
                      onChange={(e) => {
                        setDefaultShippingId(e.target.value);
                        if (errorMessage) setErrorMessage('');
                      }}
                      onBlur={() => setSenderIdTouched(true)}
                      style={senderIdTouched && !defaultShippingId.trim() ? { borderColor: 'var(--err)' } : undefined}
                    />
                  </Field>
                  <Field
                    label="Sender Name"
                    hint={senderNameTouched && !defaultShippingName.trim() ? 'Sender name is required' : undefined}
                  >
                    <Input
                      value={defaultShippingName}
                      onChange={(e) => {
                        setDefaultShippingName(e.target.value);
                        if (errorMessage) setErrorMessage('');
                      }}
                      onBlur={() => setSenderNameTouched(true)}
                      style={senderNameTouched && !defaultShippingName.trim() ? { borderColor: 'var(--err)' } : undefined}
                    />
                  </Field>
                </div>
              </div>
            </div>

            {/* Shipping Details Description */}
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', background: 'var(--surface-2)', fontWeight: 600, fontSize: 14 }}>
                Short Description of the Shipping Details
              </div>
              <div style={{ padding: 16 }}>
                <Textarea
                  value={shippingDescription}
                  onChange={(e) => setShippingDescription(e.target.value)}
                  rows={2}
                  placeholder="e.g. Personal gift items, no commercial value"
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </div>
            </div>

            {/* Package Details */}
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', background: 'var(--surface-2)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>Package Details</span>
                <Badge tone="default">{parcelDetails.packageType}</Badge>
              </div>
              <div style={{ padding: 16 }}>
                {parcelDetails.packageType === 'package' ? (
                  <div>
                    <div className="muted" style={{ fontSize: 13, marginBottom: 10 }}>
                      Total Parcels: {parcels.length}
                    </div>
                    {parcels.map((parcel, index) => (
                      <div
                        key={parcel.id}
                        style={{
                          marginBottom: 10,
                          padding: 12,
                          background: 'var(--surface-2)',
                          borderRadius: 6,
                        }}
                      >
                        <div style={{ fontWeight: 600, fontSize: 13 }}>Parcel {index + 1}</div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          Weight: {parcel.weight} kg | Dimensions: {parcel.length}×{parcel.width}×
                          {parcel.height} cm
                        </div>
                        {parcel.fedexPackagingType && (
                          <div className="muted" style={{ fontSize: 12 }}>
                            FedEx Packaging:{' '}
                            {fedexPackagingOptions.find((opt) => opt.value === parcel.fedexPackagingType)?.label}
                          </div>
                        )}
                        {parcel.items && parcel.items.length > 0 && (
                          <div style={{ marginTop: 6 }}>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>
                              Items ({parcel.items.length}):
                            </div>
                            {parcel.items.map((item) => (
                              <div key={item.id} className="muted" style={{ fontSize: 11 }}>
                                • {item.description} (Qty: {item.quantity}, {item.weight}kg, ${item.price})
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>
                    <div className="muted" style={{ fontSize: 13, marginBottom: 10 }}>
                      Total Documents: {documents.length}
                    </div>
                    {documents.map((doc, index) => (
                      <div
                        key={doc.id}
                        style={{
                          marginBottom: 8,
                          padding: 12,
                          background: 'var(--surface-2)',
                          borderRadius: 6,
                          fontSize: 13,
                        }}
                      >
                        Document {index + 1}: {doc.quantity}x {doc.weight}kg
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Selected Service */}
            {selectedService && (
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', background: 'var(--surface-2)', fontWeight: 600, fontSize: 14 }}>
                  Selected Service
                </div>
                <div style={{ padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{selectedService.name}</div>
                      <div className="muted" style={{ fontSize: 13 }}>by {selectedService.provider}</div>
                      <div className="muted" style={{ fontSize: 13 }}>Delivery: {selectedService.deliveryTime}</div>
                    </div>
                    <div className="mono" style={{ fontWeight: 600, fontSize: 18, color: 'var(--ok)' }}>
                      {formattedShippingCost.priceWithSymbol}
                    </div>
                  </div>
                  {selectedService.features.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {selectedService.features.slice(0, 3).map((f: string, i: number) => (
                        <Badge key={i} tone="info">{f}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Additional Details */}
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', background: 'var(--surface-2)', fontWeight: 600, fontSize: 14 }}>
                Additional Details
              </div>
              <div style={{ padding: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {[
                    ['Shipment Purpose', shipmentPurpose],
                    ['VAT Type', vatType],
                    ['VAT Number', vatNumber],
                    ['Signature Required', signatureRequired ? 'Yes' : 'No'],
                    ...(parcelDetails.packageType === 'package' && totalItemValue > 0
                      ? [['Total Item Value', `$${totalItemValue.toFixed(2)}`]]
                      : []),
                  ].map(([label, value]) => (
                    <div key={label}>
                      <div style={{ fontWeight: 600, fontSize: 12 }}>{label}</div>
                      <div className="muted" style={{ fontSize: 12 }}>{value}</div>
                    </div>
                  ))}
                </div>

                <Divider style={{ margin: '12px 0' }} />

                <label
                  style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}
                >
                  <input
                    type="checkbox"
                    checked={signatureRequired}
                    onChange={(e) => setSignatureRequired(e.target.checked)}
                  />
                  <span>Require signature on delivery</span>
                  {signatureRequired && (
                    <span className="muted" style={{ fontSize: 12 }}>
                      (+{formattedSignatureCost.priceWithSymbol})
                    </span>
                  )}
                </label>
              </div>
            </div>
          </div>

          {/* Right column — Pricing summary */}
          <div>
            <Card
              pad
              style={{
                padding: 20,
                position: 'sticky',
                top: 96,
                background: 'var(--surface)',
                border: '1px solid var(--ok, #a7f3d0)',
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 10, letterSpacing: '0.05em' }}>
                PRICING SUMMARY
              </div>

              {[
                ['Shipping Cost', formattedShippingCost.priceWithSymbol],
                ...(parcelDetails.packageType === 'package' && parcelDetails.insuranceRequired
                  ? [['Insurance (1.5%)', formattedInsuranceCost.priceWithSymbol]]
                  : []),
                ...(signatureRequired && parcelDetails.packageType === 'package'
                  ? [['Signature Required', formattedSignatureCost.priceWithSymbol]]
                  : []),
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '5px 0',
                    fontSize: 13,
                  }}
                >
                  <span className="muted">{label}</span>
                  <span className="mono">{value}</span>
                </div>
              ))}

              <Divider style={{ margin: '12px 0' }} />

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>Total</span>
                <span className="mono" style={{ fontWeight: 700, fontSize: 20, color: 'var(--ok)' }}>
                  {formattedTotalCost.priceWithSymbol}
                </span>
              </div>

              <Button
                variant="blue"
                block
                size="lg"
                style={{ marginTop: 20 }}
                onClick={handleProceedToPayment}
                disabled={isUpdatingRates || !stepValidation[ShippingSteps.SUMMARY]}
              >
                {isUpdatingRates ? (
                  <span>Processing…</span>
                ) : (
                  <>
                    <span>Proceed to Payment</span>
                    <Icon name="arrowRight" size={14} />
                  </>
                )}
              </Button>

              <Callout tone="ok" style={{ marginTop: 14 }}>
                <Icon name="shield" size={13} />
                <span style={{ fontSize: 12 }}>
                  Secure checkout · Insurance included
                </span>
              </Callout>
            </Card>
          </div>
        </div>
      </Card>
    </div>
  );
};
